import { useParams } from "react-router-dom"
import { useAppContext } from "../../../hooks/appContext"
import { useLongPress } from "../../../hooks/useLongPress"
import { useGetProfileQuery } from "../../../store/messagingSlice"
import { ProfilePicture } from "../common/ProfilePicture/ProfilePicture"
import { useMessagingView } from "../hooks"
import { MessageMenu } from "../MessageMenu/MessageMenu"
import { MessageReactions } from "../MessageReactions"
import { RenderedMessage } from "../RenderedMessage/RenderedMessage"
import { TipMessage } from "../TipMessage/TipMessage"
import { isMsgFutureRlsActive } from "../toggleUtils"
import { EmojiData, type Message } from "../types"
import { classNames } from "../utils"
import styles from "./Message.module.scss"
import type { IEmoticon } from "../RenderedMessage/utils"

enum MessageType {
    AFTER_RECEIVED = "afterReceived",
    AFTER_SENT = "afterSent",
}

type Props = {
    message: Message
    showAvatar: boolean
    showMenuMessageId: string | null
    setShowMenuMessageId: (messageId: string | null) => void
    previousMessage?: Message | null
    hasTimestamp?: boolean
    setReactionMessage: (reactions: Message | undefined) => void
    setShowEmoticonDetails: (emoticonSrc: IEmoticon) => void
}

export function Message({
    message,
    showAvatar,
    showMenuMessageId,
    setShowMenuMessageId,
    previousMessage,
    hasTimestamp,
    setReactionMessage,
    setShowEmoticonDetails,
}: Props) {
    const { username } = useParams<{ username: string }>()
    const isReceived = message?.from_user?.username === message?.other_user

    // Retrieve the cached profile data of the other user from the cache
    const { data: otherUserProfile } = useGetProfileQuery(
        message.from_user.username || "",
    )

    const messagingView = useMessagingView()
    const isFutureRlsEnabled = isMsgFutureRlsActive()

    const { ref } = useLongPress({
        onLongPress: () => {
            if (isFutureRlsEnabled) {
                setShowMenuMessageId(message.i)
            }
        },
    })

    const onCloseMenu = () => {
        setShowMenuMessageId(null)
    }
    const showMenu = showMenuMessageId === message.i

    const maybeShowEmoticonDetails = (emoticon: IEmoticon) => {
        if (!showMenu) {
            setShowEmoticonDetails(emoticon)
        }
    }

    const prevMessageType =
        !hasTimestamp && previousMessage
            ? previousMessage.from_user?.username === username
                ? MessageType.AFTER_RECEIVED
                : MessageType.AFTER_SENT
            : undefined

    const reactionsCount = message.reactions
        ? Object.keys(message.reactions).length
        : 0
    const reactions: Record<string, string> = {}
    const { context: appContext } = useAppContext()
    const thisUser = appContext.logged_in_user?.username
    if (thisUser !== undefined && message.reactions !== undefined) {
        for (const [key, value] of Object.entries(message.reactions)) {
            const emoji = EmojiData.get(value)
            reactions[key] = emoji ?? ""
        }
    }

    const avatarUser = isReceived
        ? (otherUserProfile?.user ?? message.from_user)
        : message.from_user

    return (
        <div
            className={classNames(styles.message, {
                [styles.received]: isReceived,
                [styles.sent]: !isReceived,
                [styles.mobile]: messagingView.isMobile,
                [styles.desktop]: messagingView.isDesktopConversation,
                [styles.desktopUnified]: messagingView.isDesktopUnified,
                [styles.tip]: Boolean(message.tip_amount),
                [styles.hasReactions]: reactionsCount > 0,
                ...(prevMessageType && { [styles[prevMessageType]]: true }),
            })}
            data-testid="message"
        >
            <div ref={ref}>
                {showMenu && (
                    <MessageMenu
                        message={message}
                        onClose={onCloseMenu}
                        triggerRef={ref}
                    />
                )}
                {showAvatar && username && (
                    <div className={styles.avatarContainer}>
                        <ProfilePicture user={avatarUser} size={24} />
                    </div>
                )}
                {message.tip_amount ? (
                    <TipMessage
                        amount={message.tip_amount}
                        isFromCurrentUser={!isReceived}
                        message={message.m}
                    />
                ) : (
                    <RenderedMessage
                        message={message}
                        setShowEmoticonDetails={maybeShowEmoticonDetails}
                    />
                )}
            </div>
            {reactionsCount > 0 && (
                <MessageReactions
                    setReactionMessage={setReactionMessage}
                    message={message}
                />
            )}
        </div>
    )
}
